<template>
    <div class="bg-gray-50 rounded-lg shadow-md">
        <div class="p-6">
            <form @submit.prevent="update">
                <!-- Agent/Landlord Information Section -->
                <div class="bg-white rounded-md shadow-sm border border-gray-200 mb-6">
                    <div class="py-4 px-6 border-b border-gray-200">
                        <h3 class="text-xl font-bold text-gray-700">{{ recipientHeading }} Information</h3>
                        <p class="text-sm text-gray-500 mt-1">Review the {{ recipientNoun }} who will receive your report</p>
                    </div>
                    <div class="p-6">
                        <div class="bg-blue-50 rounded-lg p-4 mb-4">
                            <p v-if="property.required_two_step && !isTwoStep" class="text-sm text-gray-700 mb-3">
                                Only your <strong>Credit Report</strong> will be shared with the following {{ recipientHeading }}:
                            </p>
                            <p v-else-if="property.required_two_step && isTwoStep" class="text-sm text-gray-700 mb-3">
                                Your <strong>Background Report</strong> will be shared with the following {{ recipientHeading }}:
                            </p>
                            <p v-else class="text-sm text-gray-700 mb-3">
                                Your <strong>Credit and Background Report</strong> will be shared with the following {{ recipientHeading }}:
                            </p>
                            
                            <div v-if="application.agent.company_name" class="space-y-2">
                                <p class="text-sm"><span class="font-semibold text-gray-700">Company:</span> {{ application.agent.company_name }}</p>
                                <p class="text-sm text-gray-500">Your report will be shared with this company, and its authorized team members will be able to view it.</p>
                            </div>
                            <div v-else class="space-y-2">
                                <p class="text-sm"><span class="font-semibold text-gray-700">Name:</span> {{ application.agent.name }}</p>
                                <p class="text-sm"><span class="font-semibold text-gray-700">Email:</span> {{ application.agent.email }}</p>
                                <p v-if="application.agent.brokerage_name" class="text-sm"><span class="font-semibold text-gray-700">Agency:</span> {{ application.agent.brokerage_name }}</p>
                            </div>
                        </div>

                        <!-- Landlord not verified warning -->
                        <div v-if="!landlordVerified" class="rounded-md bg-yellow-50 p-4">
                            <div class="flex">
                                <div class="flex-shrink-0">
                                    <svg class="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                        <path fill-rule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z" clip-rule="evenodd" />
                                    </svg>
                                </div>
                                <div class="ml-3">
                                    <h3 class="text-sm font-medium text-yellow-800">Unable to Share Credit Report</h3>
                                    <p class="mt-1 text-sm text-yellow-700">
                                        Your landlord must complete identity re-verification before credit reports can be shared.
                                        Please contact your landlord to complete this step.
                                    </p>
                                </div>
                            </div>
                        </div>

                        <!-- Share recovery (RAC-10088) -->
                        <div class="rounded-md bg-yellow-50 p-4 mb-4">
                            <div class="flex">
                                <div class="flex-shrink-0">
                                    <svg class="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                        <path fill-rule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z" clip-rule="evenodd" />
                                    </svg>
                                </div>
                                <div class="ml-3">
                                    <p class="text-sm text-yellow-800">
                                        If you are unable to share the report with the landlord for any reason,
                                        <inertia-link
                                            :href="route('application.share.recovery', application.id)"
                                            class="font-semibold underline text-yellow-900 hover:text-yellow-950"
                                        >
                                            click here to re-enter your personal information and generate a new report
                                        </inertia-link>.
                                    </p>
                                </div>
                            </div>
                        </div>

                        <!-- Two-Step Warning -->
                        <div v-if="isTwoStep" class="rounded-md bg-yellow-50 p-4 mb-4">
                            <div class="flex">
                                <div class="flex-shrink-0">
                                    <svg class="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                        <path fill-rule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z" clip-rule="evenodd" />
                                    </svg>
                                </div>
                                <div class="ml-3">
                                    <h3 class="text-sm font-medium text-yellow-800">Attention</h3>
                                    <p class="mt-1 text-sm text-yellow-700">You are about to share your criminal record with your agent.</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Fair Credit Reporting Act Authorization -->
                <div class="bg-white rounded-md shadow-sm border border-gray-200 mb-6">
                    <div class="py-4 px-6 border-b border-gray-200">
                        <h3 class="text-xl font-bold text-gray-700">Fair Credit Reporting Act Authorization</h3>
                        <p class="text-sm text-gray-500 mt-1">Authorize RentRisk to access and share your report</p>
                    </div>
                    <div class="p-6">
                        <div class="bg-gray-50 rounded-lg p-4 mb-4">
                            <p class="text-sm text-gray-700 leading-relaxed">
                                You understand that by clicking on the "I Agree" checkbox below, you are providing "written instructions" to RentRisk 
                                under the Fair Credit Reporting Act authorizing RentRisk to obtain information from your personal credit report or other 
                                information from Experian. You authorize RentRisk to obtain such information to confirm your identity, access, and share 
                                your credit report and Vantage Score credit score with an end user whom you have authorized to view your report and score 
                                for tenant screening purposes.
                            </p>
                        </div>
                        
                        <label class="flex items-start space-x-3">
                            <input 
                                type="checkbox" 
                                v-model="form.agree" 
                                class="mt-1 rounded border-gray-300 text-rent-risk-blue-400 focus:ring-rent-risk-blue-400 h-4 w-4"
                            />
                            <span class="text-sm font-medium text-gray-700">
                                I agree to the Fair Credit Reporting Act authorization
                            </span>
                        </label>
                        <p v-if="form.errors.agree" class="text-red-500 text-sm mt-2 ml-7">{{ form.errors.agree }}</p>
                    </div>
                </div>

                <!-- Application Authorization -->
                <div class="bg-white rounded-md shadow-sm border border-gray-200 mb-6">
                    <div class="py-4 px-6 border-b border-gray-200">
                        <h3 class="text-xl font-bold text-gray-700">Application Authorization</h3>
                        <p class="text-sm text-gray-500 mt-1">Authorize verification and sharing of your application information</p>
                    </div>
                    <div class="p-6">
                        <div class="bg-gray-50 rounded-lg p-4 mb-4">
                            <p class="text-sm text-gray-700 leading-relaxed">
                                I acknowledge the conditions in this Application and authorize the Listing Broker or landlord processing this Application 
                                to verify any information contained herein and to perform any credit or investigative inquiries necessary to properly 
                                evaluate this Application, to include rental history verification, and to share with Landlord or designated representative 
                                the information necessary to make a determination.
                            </p>
                        </div>
                        
                        <label class="flex items-start space-x-3">
                            <input 
                                type="checkbox" 
                                v-model="form.authorization_signature" 
                                class="mt-1 rounded border-gray-300 text-rent-risk-blue-400 focus:ring-rent-risk-blue-400 h-4 w-4"
                            />
                            <span class="text-sm font-medium text-gray-700">
                                I agree to the application authorization
                            </span>
                        </label>
                        <p v-if="form.errors.authorization_signature" class="text-red-500 text-sm mt-2 ml-7">{{ form.errors.authorization_signature }}</p>
                    </div>
                </div>

                <!-- Digital Signature Section -->
                <div class="bg-white rounded-md shadow-sm border border-gray-200 mb-6">
                    <div class="py-4 px-6 border-b border-gray-200">
                        <h3 class="text-xl font-bold text-gray-700">Authorization Signature</h3>
                        <p class="text-sm text-gray-500 mt-1">Please sign below to authorize the sharing of your report</p>
                    </div>
                    <div class="p-6">
                        <label class="block text-sm font-medium text-gray-700 mb-2">Digital Signature <span class="text-red-500">*</span></label>
                        <div class="border-2 border-gray-300 rounded-lg overflow-hidden">
                            <Vue3SignatureEs 
                                v-model="form.signature" 
                                ref="signature" 
                                :sigOption="option" 
                                :w="'100%'" 
                                :h="'200px'"
                                :disabled="disabled"
                            />
                        </div>
                        <div class="mt-3 flex items-center justify-between">
                            <button 
                                @click="undo" 
                                type="button" 
                                class="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-rent-risk-blue-400"
                            >
                                <svg class="-ml-0.5 mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6"></path>
                                </svg>
                                Undo
                            </button>
                            <span class="text-sm text-gray-500">Draw your signature above</span>
                        </div>
                        <p v-if="form.errors.signature" class="text-red-500 text-sm mt-2">{{ form.errors.signature }}</p>
                    </div>
                </div>

                <!-- Footer with Submit Button -->
                <div class="bg-white rounded-md shadow-md border border-gray-200">
                    <div class="border-t border-gray-200 px-6 py-4">
                        <div class="flex items-center justify-between">
                            <div class="text-sm text-gray-500">
                                <span v-if="!landlordVerified" class="text-yellow-700 font-medium">
                                    Your landlord must verify their identity before you can share
                                </span>
                                <span v-else-if="!form.agree || !form.authorization_signature">
                                    Please complete all required fields before submitting
                                </span>
                                <span v-else class="text-green-600 font-medium">
                                    ✓ Ready to share your report
                                </span>
                            </div>
                            <div class="flex items-center space-x-3">
                                <inertia-link
                                    :href="route('application.steps', application.id)"
                                    class="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-rent-risk-blue-400"
                                >
                                    Cancel
                                </inertia-link>
                                <div class="flex items-center">
                                    <form-button :form="form" type="submit" :disabled="!landlordVerified">
                                        Share Report
                                    </form-button>
                                    <tool-tip
                                        v-if="!landlordVerified"
                                        title="Unable to Share Credit Report"
                                        description="Your landlord must complete identity re-verification before credit reports can be shared. Please contact your landlord to complete this step."
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </form>
        </div>

        <modal :open="isRepeatedShareFailureModalOpen" @closeAlert="isRepeatedShareFailureModalOpen = false">
            <div class="text-xl font-semibold leading-6 text-gray-900 flex items-center justify-center gap-x-2 mb-5">
                <div class="flex h-10 w-10 items-center justify-center rounded-full bg-yellow-100">
                    <font-awesome-icon :icon="faExclamation" class="h-6 w-6 text-yellow-500" aria-hidden="true" />
                </div>
                <span>Having Trouble Sharing?</span>
            </div>
            <p class="text-sm text-gray-700 text-left">
                We've noticed you've tried sharing your report a few times. Try re-entering your personal details and then re-sharing your report.
            </p>
            <div class="mt-6 flex flex-col gap-3 sm:flex-row sm:justify-center">
                <inertia-link
                    :href="route('application.share.recovery', application.id)"
                    class="primaryButton inline-flex justify-center"
                >
                    Re-enter Personal Details
                </inertia-link>
            </div>
        </modal>
    </div>
</template>

<script>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faExclamation } from '@fortawesome/pro-light-svg-icons'
import FormButton from '@/Shared/FormButton.vue'
import Vue3SignatureEs from "vue3-signature";
import AlertCard from "@/Shared/AlertCard.vue";
import ToolTip from "@/Shared/ToolTip.vue";
import Modal from "@/Shared/Modal.vue";

export default {
    components: {
        Vue3SignatureEs,
        FormButton,
        AlertCard,
        ToolTip,
        Modal,
        FontAwesomeIcon,
    },
    props: {
        property: Object,
        application: Object,
        isTwoStep: Boolean,
        showRepeatedShareFailureNudge: {
            type: Boolean,
            default: false,
        },
        step_completion_status: Object,
        step_progress_data: Object,
        completed_steps_map: Object,
    },
    computed: {
        landlordVerified() {
            return Boolean(this.application.agent?.is_experian_verified);
        },
        recipientHeading() {
            return this.application.agent?.company_name ? 'Company' : 'Agent/Landlord';
        },
        recipientNoun() {
            return this.application.agent?.company_name ? 'company' : 'agent/landlord';
        },
    },
    data() {
        return {
            faExclamation,
            isRepeatedShareFailureModalOpen: false,
            disabled: false,
            option: {
                penColor: "rgb(0, 0, 0)",
                backgroundColor: "rgb(255,255,255)"
            },
            form: this.$inertia.form({
                agree: null,
                signature: null,
                authorization_signature: null,
                isTwoStep: this.isTwoStep,
            }),
        }
    },
    mounted() {
        this.openRepeatedShareFailureModalIfNeeded();
    },
    watch: {
        showRepeatedShareFailureNudge(show) {
            if (show) {
                this.isRepeatedShareFailureModalOpen = true;
            }
        },
        '$page.props.flash.show_repeated_share_failure_nudge'(show) {
            if (show) {
                this.isRepeatedShareFailureModalOpen = true;
            }
        },
    },
    methods: {
        openRepeatedShareFailureModalIfNeeded() {
            if (
                this.showRepeatedShareFailureNudge
                || this.$page.props.flash?.show_repeated_share_failure_nudge
            ) {
                this.isRepeatedShareFailureModalOpen = true;
            }
        },
        update() {
            if(!this.landlordVerified) {
                return false;
            }
            if(this.$refs.signature.isEmpty()) {
                alert('Please sign your name in the Authorization Signature box.')
                return false;
            }
            this.form.signature = this.$refs.signature.save();
            this.form.post(this.route('application.share.submit', this.application.id), {
                preserveScroll: true,
                onFinish: () => {
                    this.openRepeatedShareFailureModalIfNeeded();
                },
            });
        },
        undo() {
            this.$refs.signature.undo();
        },
    },
}
</script>